/** Avatar muharriri (demo v3 `AvatarEditor`): avatar yasash (fon, teri, soch, ko'z, og'iz, aksessuar) yoki surat yuklash. Stek ekrani. */
import { useState } from 'react';
import { useStore, type AvCfg, type Avatar } from '../store';
import { nav } from '../lib/nav';
import { toast } from '../lib/toast';
import { chime } from '../lib/sound';
import { t } from '../i18n';
import { Ic } from '../ui/Icon';
import { AVOPT, AV_DEFAULT, avSvg } from '../ui/Avatar';
import { saveAvatar } from './Profile';

/** Rasmni kvadrat qilib qirqib, 256×256 JPEG data URL'ga aylantiradi. */
function squareJpeg(f: File): Promise<string> {
  return new Promise((res, rej) => {
    const rd = new FileReader();
    rd.onerror = () => rej(new Error('read'));
    rd.onload = () => {
      const im = new Image();
      im.onerror = () => rej(new Error('image'));
      im.onload = () => {
        const s = Math.min(im.width, im.height), cv = document.createElement('canvas');
        cv.width = cv.height = 256;
        cv.getContext('2d')!.drawImage(im, (im.width - s) / 2, (im.height - s) / 2, s, s, 0, 0, 256, 256);
        res(cv.toDataURL('image/jpeg', 0.85));
      };
      im.src = rd.result as string;
    };
    rd.readAsDataURL(f);
  });
}

type RowKey = keyof AvCfg;

export function AvatarEditor() {
  const cur = useStore((s) => s.avatar);
  const xp = useStore((s) => s.xp);
  const [cfg, setCfg] = useState<AvCfg>(() => ({ ...AV_DEFAULT, ...(cur?.t === 'build' ? cur.c : {}) }));
  const [mode, setMode] = useState<'build' | 'photo'>(cur?.t === 'photo' ? 'photo' : 'build');
  const [photo, setPhoto] = useState<string | null>(cur?.t === 'photo' ? cur.d : null);

  const row = (k: RowKey, label: string, list: ReadonlyArray<string | [string, number]>, swatch = false) => (
    <div className="col g6">
      <span className="cap">{t(label)}</span>
      <div className="avopts">
        {list.map((v, i) => {
          const need = Array.isArray(v) ? v[1] : 0, lock = k === 'acc' && need > 0 && xp < need;
          const name = Array.isArray(v) ? v[0] : v;
          return (
            <button key={i} className={`avo ${cfg[k] === i ? 'on' : ''} ${swatch ? 'sw' : ''}`} disabled={lock}
              style={swatch ? { background: name } : undefined} aria-label={swatch ? `${t(label)} ${i + 1}` : undefined}
              onClick={() => setCfg({ ...cfg, [k]: i })}>
              {swatch ? null : t(name)}{lock && <> <Ic n="lock" size={10} /> {need / 1000}k</>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const rnd = () => {
    const r = (a: readonly unknown[]) => Math.floor(Math.random() * a.length);
    setCfg({ bg: r(AVOPT.bg), skin: r(AVOPT.skin), hair: r(AVOPT.hair), hairC: r(AVOPT.hairC), eyes: r(AVOPT.eyes), mouth: r(AVOPT.mouth), acc: Math.random() < 0.5 ? 0 : 1 + Math.floor(Math.random() * 2) });
  };
  const pick = async (f: File | undefined) => {
    if (!f) return;
    try { setPhoto(await squareJpeg(f)); } catch { toast(t('Rasmni ochib boʻlmadi')); }
  };
  const save = () => {
    const a: Avatar | null = mode === 'build' ? { t: 'build', c: cfg } : photo ? { t: 'photo', d: photo } : null;
    if (!a) return;
    saveAvatar(a); chime(); toast(t('Avatar saqlandi')); nav.pop();
  };
  const remove = () => { saveAvatar(null); setPhoto(null); toast(t('Avatar olib tashlandi')); };

  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Orqaga')} onClick={() => nav.pop()}><Ic n="back" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t('Avatar')}</b>
      </div>
      <div className="sbody fade" style={{ gap: 14 }}>
        <div className="avprev">
          {mode === 'photo'
            ? (photo ? <img src={photo} alt="" /> : <span className="phph"><Ic n="image" size={40} /></span>)
            : <span style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: avSvg(cfg, 150) }} />}
        </div>
        <div className="seg" role="group">
          <button aria-pressed={mode === 'build'} onClick={() => setMode('build')}>{t('Avatar yasash')}</button>
          <button aria-pressed={mode === 'photo'} onClick={() => setMode('photo')}>{t('Surat yuklash')}</button>
        </div>
        {mode === 'build' ? (
          <>
            {row('bg', 'Fon', AVOPT.bg, true)}
            {row('skin', 'Teri rangi', AVOPT.skin, true)}
            {row('hair', 'Soch / bosh kiyim', AVOPT.hair)}
            {row('hairC', 'Soch rangi', AVOPT.hairC, true)}
            {row('eyes', 'Koʻzlar', AVOPT.eyes)}
            {row('mouth', 'Ogʻiz', AVOPT.mouth)}
            {row('acc', 'Aksessuar', AVOPT.acc)}
            <button className="btn soft" onClick={rnd}><Ic n="shuffle" size={18} />{t('Tasodifiy')}</button>
          </>
        ) : (
          <>
            <label className="btn" style={{ cursor: 'pointer' }}><Ic n="image" size={18} />{t('Galereyadan tanlash')}
              <input type="file" accept="image/*" hidden onChange={(e) => void pick(e.target.files?.[0])} /></label>
            <p className="t12" style={{ margin: 0, textAlign: 'center' }}>{t('Rasm kvadrat qilib qirqiladi va 256×256 px ga kichraytiriladi.')}</p>
            {cur && <button className="btn soft" style={{ color: 'var(--error-ink)' }} onClick={remove}>{t('Avatarni olib tashlash')}</button>}
          </>
        )}
      </div>
      <div className="sfoot"><button className="btn" disabled={mode === 'photo' && !photo} onClick={save}>{t('Saqlash')}</button></div>
    </>
  );
}
